import { asArray } from './promoPrimitives';
import { findFreeRow, findMainRow, getCampaign, getOffer, negoRowsOf } from './promoModel';
import { freeConfig } from './promoFree';
import { offerErrors } from './promoValidation';
import { getOfferForecastState, isOrderBlocked } from './promoForecast';

// Ordering from the promo step (#23521) : the per-line cart icons and the offer "Commander" button. Port of
// `addPromoProductToCartHandler` / `addAllToCartHandler` / `hasOrderableProducts` (visitStep3-promo.js:1400, 5195-5314).
// The cart itself stays legacy (shoppingCart.js) : these rules only decide what may be sent to it.

/** The offer lists at least one orderable product (paying or free) : the Commander button is shown. */
export const hasOrderableProducts = (offer) => asArray(offer?.products).some((p) => p.orderable || p.freeOrderable);

/** RG-14 : while the forecast of the offer is on, the Commander button and the cart icons are absent. */
export const isOrderHidden = (snapshot, ref) => isOrderBlocked(getOfferForecastState(snapshot, getCampaign(snapshot, ref.campaignId), ref));

/** A line that may go to the cart : volume and container set (the legacy crashed on a missing line, JS:5210). */
const isComplete = (row) => !!row && row.volume > 0 && !!row.productContainerId;

/** Whether the cart icon of a line is shown (paying : `orderable`, free : `freeOrderable`). */
export function isLineOrderable(snapshot, ref, productId, isFree = false) {
	if (snapshot?.readonly || isOrderHidden(snapshot, ref)) return false;
	const offer = getOffer(getCampaign(snapshot, ref.campaignId), ref.campaignOfferId);
	const config = freeConfig(offer, productId);
	return isFree ? config.freeOrderable : config.orderable;
}

/**
 * Checks a line before adding it to the cart : no validation error on the offer (`CannotOrderWithErrors`, the legacy
 * looked for a visible `.local-promo-error`), then a quantity and a container.
 */
export function checkLineOrder(snapshot, validation, ref, productId, isFree = false) {
	if (isOrderHidden(snapshot, ref)) return { ok: false, error: 'PromoForecastOrderBlocked' };
	if (offerErrors(validation, ref).length) return { ok: false, error: 'CannotOrderWithErrors' };
	const rows = negoRowsOf(snapshot, ref);
	const row = isFree ? findFreeRow(rows, ref, productId) : findMainRow(rows, ref, productId);
	if (!row || !row.volume) return { ok: false, error: 'Cannot add to cart : quantity is 0.' };
	if (!row.productContainerId) return { ok: false, error: 'No products added : none are available.' };
	return { ok: true, row };
}

/** RG-12 : the Commander button stays disabled while no product of the offer has a negotiated volume. */
export function canOrderOffer(snapshot, ref) {
	if (snapshot?.readonly || isOrderHidden(snapshot, ref)) return false;
	const offer = getOffer(getCampaign(snapshot, ref.campaignId), ref.campaignOfferId);
	if (!hasOrderableProducts(offer)) return false;
	return negoRowsOf(snapshot, ref).some((row) => !row.additional && isComplete(row));
}
